import React from 'react';
import { createBottomTabNavigator, type BottomTabBarProps } from '@react-navigation/bottom-tabs';
import type { NavigatorScreenParams } from '@react-navigation/native';
import { BottomNav } from '../components/BottomNav';
import { BarreLaterale } from '../components/BarreLaterale';
import { useGabarit } from '../hooks/useGabarit';
import { interfaceRtl } from '../i18n/sens';
import { SCENES_ONGLETS } from './screenOptions';
import { CvNavigator, type CvStackParamList } from './CvNavigator';
import { ProfileNavigator, type ProfileStackParamList } from './ProfileNavigator';
import { OffersScreen } from '../screens/OffersScreen';
import { NewsScreen } from '../screens/NewsScreen';
import type { TabKey } from '../types';

export type TabParamList = {
  CVs: NavigatorScreenParams<CvStackParamList> | undefined;
  Offers: undefined;
  News: undefined;
  Profile: NavigatorScreenParams<ProfileStackParamList> | undefined;
};

const ROUTE_BY_KEY: Record<TabKey, keyof TabParamList> = {
  cvs: 'CVs',
  offers: 'Offers',
  news: 'News',
  profile: 'Profile',
};

const KEY_BY_ROUTE: Record<keyof TabParamList, TabKey> = {
  CVs: 'cvs',
  Offers: 'offers',
  News: 'news',
  Profile: 'profile',
};

const Tab = createBottomTabNavigator<TabParamList>();

// Adapte notre navigation (design system) à la barre de react-navigation : la barre du bas sur
// téléphone, la barre latérale ou le rail d'icônes sur grand écran (voir `useGabarit`).
function CustomTabBar({ state, navigation, variante }: BottomTabBarProps & { variante?: 'etendu' | 'moyen' }) {
  const activeRoute = state.routes[state.index].name as keyof TabParamList;
  const ouvrirCoaching = () => navigation.getParent()?.navigate('Coaching');
  if (variante) {
    return (
      <BarreLaterale
        variante={variante}
        activeKey={KEY_BY_ROUTE[activeRoute]}
        onTabPress={(key) => navigation.navigate(ROUTE_BY_KEY[key])}
        onCoachingPress={ouvrirCoaching}
      />
    );
  }
  return (
    <BottomNav
      activeKey={KEY_BY_ROUTE[activeRoute]}
      onTabPress={(key) => navigation.navigate(ROUTE_BY_KEY[key])}
      onFabPress={ouvrirCoaching}
    />
  );
}

export function TabNavigator() {
  const { compact, etendu } = useGabarit();
  // Sur téléphone, EXACTEMENT les options d'hier. Sur grand écran, la barre passe à gauche, ou à
  // droite en arabe : `tabBarPosition` est physique, pas logique.
  const options = compact
    ? SCENES_ONGLETS
    : { ...SCENES_ONGLETS, tabBarPosition: interfaceRtl() ? ('right' as const) : ('left' as const) };
  return (
    /* AUCUNE BORNE SUR LA SCÈNE — ET C'EST UN CORRECTIF, PAS UN OUBLI.
     *
     * Une scène bornée à 1200 est TRANSPARENTE sur ses marges, et react-navigation empile les
     * quatre onglets en `position: absolute` avec `zIndex: -1` pour les inactifs. Sur le web, le
     * détachement n'existe pas — `react-native-screens` n'a pas d'implémentation web, `MaybeScreen`
     * retombe sur une `View` et ignore `activityState` — donc un onglet inactif CONTINUE DE PEINDRE.
     * Résultat observé : la bande d'en-tête de News, pleine largeur, apparaissait dans les marges de
     * tous les autres onglets, en deux rectangles blancs collés en haut.
     *
     * La scène traverse donc toujours, et la borne redescend sur le CONTENU : les piles la
     * reçoivent par `STACK_SCREEN_OPTIONS.contentStyle`, News et Offres — seuls onglets branchés
     * sur un écran et non sur une pile — la posent eux-mêmes. Chaque onglet peint alors un fond
     * opaque d'un bord à l'autre et recouvre ce qui se trouve derrière.
     */
    <Tab.Navigator
      screenOptions={options}
      tabBar={(props) => <CustomTabBar {...props} variante={compact ? undefined : etendu ? 'etendu' : 'moyen'} />}
    >
      {/* CVs EN PREMIER, donc route initiale : aucun `initialRouteName` n'est posé ici, et rien dans
          l'application ne navigue vers un onglet par son nom au moment de la connexion. C'est
          cette ligne, et elle seule, qui décide de l'écran d'après connexion. */}
      <Tab.Screen name="CVs" component={CvNavigator} />
      <Tab.Screen name="Offers" component={OffersScreen} />
      <Tab.Screen name="News" component={NewsScreen} />
      <Tab.Screen name="Profile" component={ProfileNavigator} />
    </Tab.Navigator>
  );
}
